'use client'

import { useEffect, useMemo, useRef, useState } from 'react'
import Link from 'next/link'
import { useTheme } from '@/components/public/branding/ThemeProvider'
import { citySlug } from '@/lib/timed-ui/city-slug'

interface CityRow {
  slug: string
  name: string
  count: number
}

/**
 * Storefront navbar dropdown listing cities. Gated on
 * branding.layout.showLocationsNav — renders nothing when off.
 * Discovers cities from /api/events/public once on mount; pinned cities
 * from branding.layout.featuredLocations show first.
 *
 * Desktop only — mobile nav doesn't have room for a dropdown and the
 * homepage Locations view already serves that audience well.
 */
export default function LocationsNavMenu() {
  const { branding } = useTheme()
  const layout = (branding?.layout || {}) as Record<string, unknown>
  const enabled = layout.showLocationsNav === true
  const featured = Array.isArray(layout.featuredLocations) ? (layout.featuredLocations as string[]) : []

  const [cities, setCities] = useState<CityRow[]>([])
  const [open, setOpen] = useState(false)
  const ref = useRef<HTMLDivElement | null>(null)

  useEffect(() => {
    if (!enabled) return
    let cancelled = false
    fetch('/api/events/public')
      .then(r => r.json())
      .then(d => {
        if (cancelled || !d.success || !Array.isArray(d.events)) return
        const map = new Map<string, CityRow>()
        for (const ev of d.events) {
          const cityName = ev.venue?.city
          if (!cityName) continue
          const slug = citySlug(cityName)
          const existing = map.get(slug)
          if (existing) existing.count += 1
          else map.set(slug, { slug, name: cityName, count: 1 })
        }
        setCities(Array.from(map.values()))
      })
      .catch(() => {})
    return () => { cancelled = true }
  }, [enabled])

  // Close on outside click.
  useEffect(() => {
    if (!open) return
    function onDown(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false)
    }
    document.addEventListener('mousedown', onDown)
    return () => document.removeEventListener('mousedown', onDown)
  }, [open])

  const sorted = useMemo(() => {
    if (cities.length === 0) return []
    const featuredIndex = new Map(featured.map((s, i) => [s, i]))
    return [...cities].sort((a, b) => {
      const ai = featuredIndex.get(a.slug) ?? Infinity
      const bi = featuredIndex.get(b.slug) ?? Infinity
      if (ai !== bi) return ai - bi
      return a.name.localeCompare(b.name)
    })
  }, [cities, featured])

  if (!enabled) return null
  if (cities.length === 0) return null

  return (
    <div ref={ref} className="hidden md:block relative">
      <button
        onClick={() => setOpen(o => !o)}
        className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-text-secondary hover:text-text-primary hover:bg-surface-tertiary rounded-lg transition-colors"
        aria-expanded={open}
        aria-haspopup="true"
      >
        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
        </svg>
        Locations
        <svg className={`w-3.5 h-3.5 transition-transform ${open ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
        </svg>
      </button>

      {open && (
        <div className="absolute right-0 mt-1.5 w-64 max-h-[60vh] overflow-y-auto bg-surface border border-surface-border rounded-xl shadow-card py-1.5 z-50">
          {sorted.slice(0, 25).map(city => {
            const isFeatured = featured.includes(city.slug)
            return (
              <Link
                key={city.slug}
                href={`/locations/${city.slug}`}
                onClick={() => setOpen(false)}
                className="flex items-center justify-between gap-2 px-3 py-2 text-sm text-text-secondary hover:bg-surface-secondary hover:text-text-primary transition-colors"
              >
                <span className="flex items-center gap-2 min-w-0">
                  <span className="truncate">{city.name}</span>
                  {isFeatured && (
                    <span className="text-[9px] font-semibold uppercase tracking-wider px-1 py-0.5 bg-primary/10 text-[rgb(var(--color-primary))] rounded">
                      Featured
                    </span>
                  )}
                </span>
                <span className="text-xs text-text-tertiary flex-shrink-0">{city.count}</span>
              </Link>
            )
          })}
          {sorted.length > 25 && (
            <div className="px-3 py-1 text-[11px] text-text-tertiary">+{sorted.length - 25} more</div>
          )}
          <div className="border-t border-surface-border mt-1 pt-1">
            <Link
              href="/?view=locations"
              onClick={() => setOpen(false)}
              className="block px-3 py-2 text-sm font-medium text-[rgb(var(--color-primary))] hover:bg-surface-secondary"
            >
              Browse all locations →
            </Link>
          </div>
        </div>
      )}
    </div>
  )
}
